import { Layout, notification, Skeleton, theme } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  initBillSlice,
  setPropertiesBill,
  updateBill,
} from "../Slices/Bill.slice";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomForm from "../../../shared/components/CustomForm";
import routes from "../../../engine/routes";
import {
  billStatus,
  daysJsFormat,
  defaultDateEmission,
  keyBillStatus,
  roles,
} from "../../../utils/constants";
import { userHasLevel } from "../../../engine/AuthRoute";
import { UserContext } from "../../../engine/context/User.context";
import dayjs from "../../../utils/dayjs";
import { http } from "../../../engine/api/http";
import { BillHeader } from "../../../shared/components/BillHeader/BillHeader";
import { useTranslation } from "react-i18next";

const { Content, Sider, Header, Footer } = Layout;

export function BillEdit({ unitId, expanded, onClose }) {
  const { id } = useParams();
  const { bill, loading } = useSelector(state => state.bill);

  const { user } = useContext(UserContext);
  const { t } = useTranslation();

  const { init, clear } = initBillSlice({});

  const dispatch = useDispatch();

  const formRef = useRef(null);

  const [temp, setTemp] = useState(false);

  const {
    token: { colorBgLayout },
  } = theme.useToken();

  const { date_, noteCredit = "", noteCreditAmount = 0 } = bill;

  useEffect(() => {
    setTemp(bill);
  }, [bill]);

  const onValuesChange = (changedValues, allValues) => {
    setTemp({ ...temp, ...changedValues });
  };

  const defaultDateSent = temp.date_sent
    ? dayjs(temp.date_sent)
    : dayjs(defaultDateEmission(temp), daysJsFormat.full);

  const inputs = [
    {
      parentLabel: "",
      children: [
        {
          label: t("billEdit.status"),
          name: "status",
          type: "select",
          value: bill.status,
          required: true,
          selectContent: billStatus(t),
          col: 24,
        },
        {
          label: t("billEdit.sending"),
          quote: (
            <>
              {t("billEdit.defaultDateSent", {
                date: dayjs(defaultDateEmission(bill)).format(daysJsFormat.day),
              })}
            </>
          ),
          watch: "status",
          disabled: temp.status !== keyBillStatus.sent,
          watchValues: [keyBillStatus.sent, keyBillStatus.paid],
          watchRenderCondition: true,
          name: "date_sent",
          value: defaultDateSent,
          type: "date",
          col: 12,
        },
        {
          label: t("billEdit.date_rappel_1"),
          name: "date_rappel_1",
          disabled: temp.status !== keyBillStatus.rappel_1,
          watch: "status",
          value: temp.date_rappel_1
            ? dayjs(temp.date_rappel_1)
            : dayjs(defaultDateEmission(temp)).add(30, "day"),
          quote: (
            <>
              {t("billEdit.defaultDateRappel1", {
                date: dayjs(defaultDateEmission(temp))
                  .add(30, "day")
                  .format(daysJsFormat.day),
              })}
            </>
          ),
          watchValues: [keyBillStatus.rappel_1, keyBillStatus.paid],
          watchRenderCondition: true,
          type: "date",
          col: 12,
        },
        {
          label: t("billEdit.date_rappel_2"),
          name: "date_rappel_2",
          watch: "status",
          value: temp.date_rappel_2
            ? dayjs(temp.date_rappel_2)
            : dayjs(defaultDateEmission(temp)).add(45, "day"),
          quote: (
            <>
              {t("billEdit.defaultDateRappel2", {
                date: dayjs(defaultDateEmission(temp))
                  .add(45, "day")
                  .format(daysJsFormat.day),
              })}
            </>
          ),
          disabled: temp.status !== keyBillStatus.rappel_2,
          watchValues: [keyBillStatus.rappel_2, keyBillStatus.paid],
          watchRenderCondition: true,
          type: "date",
          col: 12,
        },
        {
          label: t("billEdit.date_paid"),
          name: "date_paid",
          watch: "status",
          value: dayjs(bill.date_paid),
          disabled: temp.status !== keyBillStatus.paid,
          watchValues: [keyBillStatus.paid],
          watchRenderCondition: true,
          type: "date",
          col: 12,
        },
        {
          label: t("billEdit.billingNote"),
          name: "noteCredit",
          value: noteCredit,
          type: "input",
          maxLength: 80,
          col: 16,
        },
        {
          label: t("billEdit.billingNote"),
          name: "noteCreditAmount",
          value: noteCreditAmount,
          type: "input-currency",
          col: 8,
        },
        {
          label: t("billEdit.billingCommentPrivate"),
          name: "comment",
          type: "textarea",
          col: 24,
        },
        {
          label: t("billEdit.billingCommentInstitution"),
          name: "comment_institution",
          type: "textarea",
          col: 24,
        },
        {
          label: t("billEdit.billingCommentPaiement"),
          name: "comment_paiement",
          type: "textarea",
          col: 24,
        },
      ],
    },
    {
      children: [
        {
          label: t("common.save"),
          name: "send",
          value: "send",
          type: "submit",
          col: 24,
          align: "right",
        },
      ],
    },
  ];

  const formatString = s => {
    return Number(s.replace(/[^0-9.-]+/g, ""));
  };

  const onSubmitForm = async data => {
    await dispatch(setPropertiesBill(data));
    dispatch(updateBill());
  };

  const sendEmail = async () => {
    let caller = await http.post("/notifications/bill", { userId: id });
    caller.status === 200 &&
      notification.success({
        message: t("billEdit.emailSentSuccess"),
      });
  };

  return (
    <>
      <BillHeader
        title={routes.billing(id).detail.title + " " + bill.bill_number}
      />
      {loading ? (
        <Skeleton active />
      ) : (
        <>
          <CustomForm
            disabled={
              !userHasLevel({
                only: {
                  [roles.admin]: "crud", //
                  [roles.permanance]: "crud", //
                },
              })
            }
            initialValues={bill}
            dataSource={inputs}
            handleSubmit={data => onSubmitForm(data)}
            labelCol={24}
            refForm={formRef}
            wrapperCol={24}
            onValuesChange={onValuesChange}
          />
        </>
      )}
    </>
  );
}
